import React, {Component, FunctionComponent, useEffect, useRef, useState } from 'react';
import NavDropdown from 'react-bootstrap/NavDropdown';
import {Link, NavLink} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useOktaAuth} from '@okta/okta-react'

import './Navbar.scss';
import logoImage from 'assets/img/icon_circle-cfa-logo.svg';
import {IOktaUser} from 'interfaces';
import {UsersService} from 'services';

interface INavbarProps {
    className?: string,
}

export const Navbar: FunctionComponent<INavbarProps> = ({className}) => {
    const { t, i18n } = useTranslation();
    const {oktaAuth, authState} = useOktaAuth();
    const [user, setUser] = useState<IOktaUser>();

    // Load or clear our user info when the user logs in/out.
    // We'll keep track of whether the user was previously authenticated when we rendered last.
    const prevIsAuthedRef = useRef(false);
    useEffect(() => {
        if (!prevIsAuthedRef.current && authState && authState.isAuthenticated) {
            // If the user has just now been authenticated, we need to load their info the the navbar.
            oktaAuth.token.getUserInfo()
                .then((loadedUser: IOktaUser) => {
                    setUser(loadedUser);
                })
                .catch((err) => {
                    console.log('ERROR fetching user: ', err);
                });
        } else if (prevIsAuthedRef.current && authState && !authState.isAuthenticated) {
            // If the user was authenticated and has just now been logged out, clear our user info.
            setUser(undefined);
        }

        prevIsAuthedRef.current = authState?.isAuthenticated
    }, [authState]);
    
    function signOut() {
        UsersService.reset();
        oktaAuth.signOut();
    }

    return (
        <div className={className}>
            <Link to="/" className="navbar_logoSection" data-cy="navBar_logo">
                <img src={logoImage} className="navbar_logo"/>
                <span className="navbar_logoText">
                    {t('shared.appName')}
                </span>
            </Link>
            <div className="navbar_middleSection"></div>
            {authState && authState.isAuthenticated && (
                <div className="navbar_userSection">
                    {/* Using bootstrap dropdown with custom styling. */}
                    <NavDropdown
                        className="navbar_dropdown"
                        title={<>
                            <span className="navbar_dropdownLabel">
                                {t('navbar.userName', {name: user?.name})}
                            </span>
                            <FontAwesomeIcon icon="caret-down" />
                        </>}
                        id="user-dropdown"
                    >
                        <NavDropdown.Header className="navbar_dropdownHeader">
                            <div className="navbar_signedInMessage">
                                {t('navbar.signedInMessage')}
                            </div>
                            <div className="navbar_signedInEmail">
                                {user?.email}
                            </div>
                        </NavDropdown.Header>
                        <NavDropdown.Divider />
                        <NavDropdown.Item
                            className="navbar_dropdownItem"
                            as={(props) => (
                                <NavLink to="/account" {...props} />
                            )}
                        >
                            <FontAwesomeIcon icon="cog" className="navbar_dropdownItemIcon" />
                            {t('navbar.settings')}
                        </NavDropdown.Item>
                        <NavDropdown.Item className="navbar_dropdownItem" onClick={signOut}>
                            <FontAwesomeIcon icon="sign-out-alt" className="navbar_dropdownItemIcon" />
                            {t('navbar.signOut')}
                        </NavDropdown.Item>
                    </NavDropdown>
                </div>
            )}
        </div>
    );
};